import { useNavigate } from 'react-router-dom';
import { 
  FileEdit, 
  Megaphone, 
  Trophy, 
  Settings, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Lock 
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface JobCountsByStatus {
  draft: number;
  rfq_open: number;
  awarded: number;
  in_progress: number;
  completed_pending_review: number;
  accepted: number;
  rejected: number;
  disputed: number;
  closed: number;
}

interface JobStatusTableProps {
  data: JobCountsByStatus;
  loading: boolean;
}

const statusConfig = [
  { 
    key: 'draft' as const, 
    label: 'Draft', 
    icon: FileEdit, 
    color: 'bg-muted text-muted-foreground' 
  },
  { 
    key: 'rfq_open' as const, 
    label: 'RFQ Open', 
    icon: Megaphone, 
    color: 'bg-primary/10 text-primary' 
  },
  { 
    key: 'awarded' as const, 
    label: 'Awarded', 
    icon: Trophy, 
    color: 'bg-success/10 text-success' 
  },
  { 
    key: 'in_progress' as const, 
    label: 'In Progress', 
    icon: Settings, 
    color: 'bg-primary/10 text-primary' 
  },
  { 
    key: 'completed_pending_review' as const, 
    label: 'Pending Review', 
    icon: Clock, 
    color: 'bg-warning/10 text-warning' 
  },
  { 
    key: 'accepted' as const, 
    label: 'Accepted', 
    icon: CheckCircle, 
    color: 'bg-success/10 text-success' 
  },
  { 
    key: 'rejected' as const, 
    label: 'Rejected', 
    icon: XCircle, 
    color: 'bg-destructive/10 text-destructive' 
  },
  { 
    key: 'disputed' as const, 
    label: 'Disputed', 
    icon: AlertTriangle, 
    color: 'bg-warning/10 text-warning' 
  },
  { 
    key: 'closed' as const, 
    label: 'Closed', 
    icon: Lock, 
    color: 'bg-muted text-muted-foreground' 
  },
];

export const JobStatusTable = ({ data, loading }: JobStatusTableProps) => {
  const navigate = useNavigate();
  
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center justify-between py-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-5 w-8" />
          </div>
        ))}
      </div>
    );
  }

  const totalJobs = Object.values(data).reduce((sum, count) => sum + count, 0);

  if (totalJobs === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No jobs created yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {statusConfig.map((status) => {
        const count = data[status.key];
        const Icon = status.icon;
        
        return (
          <button
            key={status.key}
            onClick={() => navigate(`/jobs?status=${status.key}`)}
            className="w-full flex items-center justify-between py-2 px-2.5 rounded-md hover:bg-accent transition-colors text-left group"
          >
            <div className="flex items-center space-x-2.5">
              <div className={`p-1.5 rounded-md ${status.color}`}>
                <Icon className="h-4 w-4" />
              </div>
              <span className="font-medium text-sm">{status.label}</span>
            </div>
            <Badge variant="secondary" className="font-semibold">
              {count}
            </Badge>
          </button>
        );
      })}
      
      <div className="pt-2 mt-2 border-t flex items-center justify-between px-2.5">
        <span className="font-semibold text-sm">Total Jobs</span>
        <Badge className="font-semibold">{totalJobs}</Badge>
      </div>
    </div>
  );
};
